import React, { useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip } from 'recharts';
import LdpmHistoryChart from './LdpmHistoryChart';
import LdpmParameterSelector from './LdpmParameterSelector';
import LdpmMetricSelector from './LdpmMetricSelector';
import { LoadingState, ErrorState, NoDataState } from './LoadingStates';
import { useLdpmHistoryData } from '../hooks/useStockData';
import { CandleSize, Timeframe, LdpmMetricKey } from '../types/StockData';

const LdpmHistoryPage: React.FC = () => {
  // State management
  const [currentTicker, setCurrentTicker] = useState<string>('NVDA');
  const [candleSize, setCandleSize] = useState<CandleSize>(15);
  const [timeframe, setTimeframe] = useState<Timeframe>('1W');
  const [selectedMetric, setSelectedMetric] = useState<LdpmMetricKey>('volume');

  // Custom hook for data management
  const { 
    ldpmHistoryData, 
    ldpmHistoryLoading, 
    ldpmHistoryError, 
    loadLdpmHistoryData 
  } = useLdpmHistoryData(currentTicker, candleSize, timeframe);

  // Event handlers
  const handleTickerSearch = (ticker: string) => {
    setCurrentTicker(ticker.toUpperCase());
    console.log(`Searching for LDPM history for ticker: ${ticker}`);
  };

  const handleCandleSizeChange = (size: CandleSize) => {
    setCandleSize(size);
  };

  const handleTimeframeChange = (tf: Timeframe) => {
    setTimeframe(tf);
  };

  const handleMetricChange = (metric: LdpmMetricKey) => {
    setSelectedMetric(metric);
  };

  const handleRefresh = () => {
    loadLdpmHistoryData(currentTicker, candleSize, timeframe);
  };

  // Loading state
  if (ldpmHistoryLoading) {
    return <LoadingState />;
  }

  // Error state
  if (ldpmHistoryError) {
    return (
      <ErrorState 
        error={ldpmHistoryError} 
        onRetry={handleRefresh} 
      />
    );
  }

  // No data state
  if (!ldpmHistoryData || ldpmHistoryData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="container mx-auto px-4 py-8">
          <header className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">
              LDPM History Analysis
            </h1>
            <p className="text-gray-600">
              Liquidity Driven Price Movement over time
            </p>
          </header>

          <LdpmParameterSelector
            candleSize={candleSize}
            timeframe={timeframe}
            onCandleSizeChange={handleCandleSizeChange}
            onTimeframeChange={handleTimeframeChange}
            currentTicker={currentTicker}
            onTickerSearch={handleTickerSearch}
            onRefresh={handleRefresh}
            isLoading={ldpmHistoryLoading}
          />

          <NoDataState 
            message={`No LDPM history data available for ${currentTicker} with ${candleSize}m candles over ${timeframe}`}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            LDPM History Analysis
          </h1>
          <p className="text-gray-600">
            Liquidity Driven Price Movement over time for {currentTicker}
          </p>
        </header>

        {/* Parameter Selectors */}
        <LdpmParameterSelector
          candleSize={candleSize}
          timeframe={timeframe}
          onCandleSizeChange={handleCandleSizeChange}
          onTimeframeChange={handleTimeframeChange}
          currentTicker={currentTicker}
          onTickerSearch={handleTickerSearch}
          onRefresh={handleRefresh}
          isLoading={ldpmHistoryLoading}
        />

        {/* KPI Header */}
        {ldpmHistoryData && ldpmHistoryData.length > 0 && (
          (() => {
            const last = ldpmHistoryData[ldpmHistoryData.length - 1];
            const ldpmVals = ldpmHistoryData.map(d => d.ldpm).filter(v => Number.isFinite(v)).sort((a, b) => a - b);
            const percentileRank = (sorted: number[], value: number): number => {
              if (sorted.length === 0) return 0;
              let lo = 0, hi = sorted.length;
              while (lo < hi) {
                const mid = Math.floor((lo + hi) / 2);
                if (sorted[mid] <= value) lo = mid + 1; else hi = mid;
              }
              return (lo / sorted.length) * 100;
            };
            const pctl = percentileRank(ldpmVals, last.ldpm);
            const regime = pctl >= 80 ? 'Accumulation' : (pctl <= 20 ? 'Distribution' : 'Neutral');
            const regimeCls = regime === 'Accumulation' ? 'bg-green-50 text-green-700 border-green-200' : regime === 'Distribution' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-gray-50 text-gray-700 border-gray-200';
            const liqCls = last.isLiquidityGood ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200';
            return (
              <div className="bg-white rounded-lg shadow-lg p-4 mb-6 border border-gray-200">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex flex-col">
                    <span className="text-xs uppercase tracking-wide text-gray-500">Latest LDPM</span>
                    <span className="text-xl font-semibold text-gray-900">{last.ldpm.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs uppercase tracking-wide text-gray-500">Percentile</span>
                    <span className="text-xl font-semibold text-blue-700">{pctl.toFixed(0)}th</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs uppercase tracking-wide text-gray-500">Price</span>
                    <span className="text-xl font-semibold text-blue-600">${last.currentClose.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs uppercase tracking-wide text-gray-500">Distance From Price</span>
                    <span className="text-xl font-semibold text-purple-600">{last.distanceFromPrice.toFixed(2)}</span>
                  </div>
                  <div className={`px-3 py-1 rounded-lg text-sm font-semibold border ${liqCls}`}>Liquidity: {last.liquidityAssessment}</div>
                  <div className={`px-3 py-1 rounded-lg text-sm font-semibold border ${regimeCls}`}>Regime: {regime}</div>
                </div>
              </div>
            );
          })()
        )}

        {/* Metric Selector */}
        <LdpmMetricSelector
          selectedMetric={selectedMetric}
          onMetricChange={handleMetricChange}
        />

        {/* Main Chart */}
        <LdpmHistoryChart
          data={ldpmHistoryData}
          selectedMetric={selectedMetric}
          onMetricChange={handleMetricChange}
        />

        {/* Additional Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">LDPM Statistics</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Average LDPM:</span>
                <span className="font-semibold">
                  {(ldpmHistoryData.reduce((sum, d) => sum + d.ldpm, 0) / ldpmHistoryData.length).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Max LDPM:</span>
                <span className="font-semibold text-green-600">
                  {Math.max(...ldpmHistoryData.map(d => d.ldpm)).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Min LDPM:</span>
                <span className="font-semibold text-red-600">
                  {Math.min(...ldpmHistoryData.map(d => d.ldpm)).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Liquidity Assessment</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Good Liquidity:</span>
                <span className="font-semibold text-green-600">
                  {ldpmHistoryData.filter(d => d.isLiquidityGood).length} 
                  ({((ldpmHistoryData.filter(d => d.isLiquidityGood).length / ldpmHistoryData.length) * 100).toFixed(1)}%)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Poor Liquidity:</span>
                <span className="font-semibold text-red-600">
                  {ldpmHistoryData.filter(d => !d.isLiquidityGood).length}
                  ({((ldpmHistoryData.filter(d => !d.isLiquidityGood).length / ldpmHistoryData.length) * 100).toFixed(1)}%)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg Distance:</span>
                <span className="font-semibold">
                  {(ldpmHistoryData.reduce((sum, d) => sum + d.distanceFromPrice, 0) / ldpmHistoryData.length).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* LDPM Distribution (current window) */}
        <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">LDPM Distribution (current window)</h3>
          {(() => {
            const values = ldpmHistoryData.map(d => d.ldpm).filter(v => Number.isFinite(v));
            if (values.length < 3) return <div className="text-gray-500 text-sm">Not enough data to build distribution.</div>;
            const min = Math.min(...values);
            const max = Math.max(...values);
            const bins = 15;
            const width = (max - min) / bins || 1;
            const counts = new Array(bins).fill(0);
            for (const v of values) {
              let idx = Math.floor((v - min) / width);
              if (idx < 0) idx = 0; if (idx >= bins) idx = bins - 1;
              counts[idx]++;
            }
            const dataBins = counts.map((c, i) => ({ bin: `${(min + i * width).toFixed(2)}`, count: c }));
            const median = (() => { const s = [...values].sort((a,b)=>a-b); const m=Math.floor(s.length/2); return s.length%2? s[m]: (s[m-1]+s[m])/2; })();
            return (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dataBins} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="bin" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <RechartsTooltip formatter={(v: any) => [`${v}`, 'Count']} labelFormatter={(l: any) => `Bin start: ${l}`} />
                    <Bar dataKey="count" fill="#8b5cf6" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 border border-gray-200">Min: {min.toFixed(2)}</span>
                  <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">Median: {median.toFixed(2)}</span>
                  <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 border border-gray-200">Max: {max.toFixed(2)}</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Regime Transitions */}
        <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Regime Transitions</h3>
          {(() => {
            const values = ldpmHistoryData.map(d => d.ldpm).filter(v => Number.isFinite(v)).sort((a,b)=>a-b);
            if (values.length < 5) return <div className="text-gray-500 text-sm">Not enough data to compute regimes.</div>;
            const pct = (sorted: number[], p: number) => {
              const idx = (sorted.length - 1) * p; const lo = Math.floor(idx); const hi = Math.ceil(idx);
              if (lo === hi) return sorted[lo]; const w = idx - lo; return sorted[lo]*(1-w)+sorted[hi]*w;
            };
            const p20 = pct(values, 0.20); const p80 = pct(values, 0.80);
            const regimeOf = (v: number) => v >= p80 ? 'Accumulation' : v <= p20 ? 'Distribution' : 'Neutral';
            const data = ldpmHistoryData;
            const transitions: any[] = [];
            for (let i=1;i<data.length;i++){
              const prevR = regimeOf(data[i-1].ldpm);
              const currR = regimeOf(data[i].ldpm);
              if (prevR !== currR){
                const px0 = data[i].currentClose;
                const r = (k: number) => {
                  const j = Math.min(i + k, data.length - 1);
                  const pxk = data[j].currentClose;
                  if (!Number.isFinite(px0) || !Number.isFinite(pxk) || px0 === 0) return null;
                  return ((pxk - px0) / Math.abs(px0)) * 100;
                };
                transitions.push({
                  idx: i,
                  date: new Date((data[i].timestamp.endsWith('Z')? data[i].timestamp: data[i].timestamp+'Z')).toLocaleString('en-US', { timeZone: 'America/New_York' }),
                  from: prevR,
                  to: currR,
                  ldpm: data[i].ldpm,
                  r1: r(1), r3: r(3), r5: r(5),
                });
              }
            }
            // Sort by most recent first (date descending via index)
            transitions.sort((a, b) => b.idx - a.idx);
            if (transitions.length === 0) return <div className="text-gray-500 text-sm">No regime transitions in the current window.</div>;
            const fmt = (v: number | null) => v==null? 'N/A' : `${v>=0? '+':''}${v.toFixed(2)}%`;
            const color = (v: number | null) => v==null? 'text-gray-700' : v>=0? 'text-green-700' : 'text-red-700';
            return (
              <div className="overflow-x-auto">
                <p className="text-xs text-gray-600 mb-2">
                  Explanation: +1 / +3 / +5 are forward returns from the transition close over the next 1, 3, and 5 bars respectively
                  (based on the current candle size and window). Values are percentages; green indicates positive, red negative, N/A when not enough data ahead.
                </p>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="py-2 pr-4">Date (ET)</th>
                      <th className="py-2 pr-4">Transition</th>
                      <th className="py-2 pr-4">LDPM</th>
                      <th className="py-2 pr-4">+1</th>
                      <th className="py-2 pr-4">+3</th>
                      <th className="py-2 pr-4">+5</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transitions.map((t, i) => (
                      <tr key={i} className={i%2===0? 'bg-gray-50':'bg-white'}>
                        <td className="py-2 pr-4 text-gray-800">{t.date}</td>
                        <td className="py-2 pr-4"><span className="font-medium text-gray-800">{t.from}</span> → <span className="font-medium text-gray-800">{t.to}</span></td>
                        <td className="py-2 pr-4 font-semibold text-purple-700">{t.ldpm.toFixed(2)}</td>
                        <td className={`py-2 pr-4 font-semibold ${color(t.r1)}`}>{fmt(t.r1)}</td>
                        <td className={`py-2 pr-4 font-semibold ${color(t.r3)}`}>{fmt(t.r3)}</td>
                        <td className={`py-2 pr-4 font-semibold ${color(t.r5)}`}>{fmt(t.r5)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
;

export default LdpmHistoryPage;
